import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getTranslate } from 'intl/translate';
import { GU } from 'components/common/graphicUploader/types';
import { useProject } from 'store/project';
import {
  ChecklistChangeObject,
  createChecklistChangeObject,
  createNodesInOrder,
  createSectionsWithHierarchy,
  hasTemplateMetadataChanges,
  mapChecklistSectionReferences,
  toChecklistItemDto,
  toCreateChecklistItemsDto,
} from 'shared/domain/template/templateDraft';
import {
  buildPreviousSectionsById,
  buildSectionsToUpdate,
  TemplateWizardSubmitResult,
  toTemplateWizardDraftPayload,
} from 'shared/domain/template/templateWizardFlow';
import { FormChecklistItem, FormSection, TemplateValidItem } from 'views/template/wizard/form/types';
import { TemplateDefaultValues } from 'views/template/wizard/populator/types';
import { useInspectionTemplate } from './useInspectionTemplate';
import { useInspectionStore } from '../store';
import { sortByFractionalOrder } from 'helpers/sort';
import {
  buildSectionUploaders,
  startTemplateCreateBackgroundUploads,
  startTemplateUpdateBackgroundSync,
  uploadCreatedChecklistItemFiles,
} from './templateWizardCommunicator.helpers';
import {
  syncAnswerChanges,
  createAnswersForNewItems,
} from './answerSyncHelpers';

export interface UseTemplateWizardCommunicatorResult {
  createTemplate: (
    template: TemplateValidItem,
    uploader: GU,
    checklist: FormChecklistItem[],
    sections: FormSection[]
  ) => Promise<TemplateWizardSubmitResult>;
  updateTemplate: (
    template: TemplateValidItem,
    uploader: GU,
    checklist: FormChecklistItem[],
    sections: FormSection[],
    templateId: string,
    previousValues: Readonly<TemplateDefaultValues>
  ) => Promise<TemplateWizardSubmitResult>;
  publishTemplate: (id: string) => Promise<TemplateWizardSubmitResult>;
}

export function useTemplateWizardCommunicator(): UseTemplateWizardCommunicatorResult {
  const project = useProject();
  const intl = useIntl();
  const translate = useMemo(() => getTranslate(intl), [intl]);
  const reloadInspectionTemplate = useInspectionStore.use.reloadInspectionTemplate();
  const {
    createInspectionTemplate,
    updateInspectionTemplate,
    createInspectionTemplateChecklistItems,
    updateInspectionTemplateChecklistItem,
    removeInspectionTemplateChecklistItem,
    reorderInspectionTemplateChecklistItem,
    publishInspectionTemplate,
    createChecklistSection,
    updateChecklistSection,
    removeChecklistSection,
    reorderChecklistSection,
    createChecklistAnswer,
    updateChecklistAnswer,
    removeChecklistAnswer,
  } = useInspectionTemplate();

  const updateTemplate = useCallback(
    (
      template: TemplateValidItem,
      uploader: GU,
      checklist: FormChecklistItem[],
      sections: FormSection[],
      templateId: string,
      previousValues: Readonly<TemplateDefaultValues>
    ): Promise<TemplateWizardSubmitResult> => {
      const templateBody = toTemplateWizardDraftPayload(template);

      const previousSectionsById = buildPreviousSectionsById(previousValues.sections);
      const sectionsToCreate = sections.filter((section) => !section._id);
      const sectionsToDelete = previousValues.sections.filter(
        (previousSection) =>
          previousSection._id &&
          !sections.some((section) => section._id === previousSection._id)
      );
      const sectionsToUpdate = buildSectionsToUpdate(sections, previousSectionsById);

      const checklistChanges: ChecklistChangeObject<FormChecklistItem> =
        createChecklistChangeObject(previousValues.checklist, checklist);

      const itemsToCreate = checklistChanges.created;

      const createNewNodesPromise =
        sectionsToCreate.length > 0 || itemsToCreate.length > 0
          ? (async () => {
              const createItemsBatch = async (items: FormChecklistItem[]) => {
                const itemsDto = toCreateChecklistItemsDto(items);
                return createInspectionTemplateChecklistItems(
                  templateId,
                  itemsDto,
                  project._id
                );
              };

              return createNodesInOrder(
                templateId,
                project._id,
                sectionsToCreate,
                itemsToCreate,
                createChecklistSection,
                createItemsBatch
              );
            })()
          : Promise.resolve({
              sectionIdMap: new Map<string, string>(),
              createdItemsByUniqueKeyId: new Map(),
            });

      return createNewNodesPromise.then(async ({
        sectionIdMap,
        createdItemsByUniqueKeyId,
      }) => {
        const saveTemplateAndChecklistPromises: Promise<unknown>[] = [];
        const templateChange = hasTemplateMetadataChanges(previousValues, templateBody);

        if (templateChange) {
          saveTemplateAndChecklistPromises.push(
            updateInspectionTemplate(templateId, templateBody, project._id)
          );
        }

        sectionsToUpdate.forEach((sectionToUpdate) => {
          saveTemplateAndChecklistPromises.push(
            updateChecklistSection({
              _id: sectionToUpdate._id!,
              title: sectionToUpdate.title,
              description: sectionToUpdate.description,
              projectId: project._id,
              inspectionTemplateId: templateId,
            })
          );
        });

        checklistChanges.changed.forEach((checklistChange) => {
          saveTemplateAndChecklistPromises.push(
            updateInspectionTemplateChecklistItem(
              templateId,
              checklistChange._id!,
              toChecklistItemDto(checklistChange),
              project._id
            )
          );
        });

        checklistChanges.deleted.forEach((checklistItemToDelete) => {
          saveTemplateAndChecklistPromises.push(
            removeInspectionTemplateChecklistItem(
              templateId,
              checklistItemToDelete._id!,
              project._id
            )
          );
        });

        await Promise.all(saveTemplateAndChecklistPromises);

        await Promise.all(
          sectionsToDelete.map((sectionToDelete) =>
            removeChecklistSection({
              _id: sectionToDelete._id!,
              projectId: project._id,
              inspectionTemplateId: templateId,
            })
          )
        );

        const answerSyncDeps = {
          translate,
          createChecklistAnswer,
          updateChecklistAnswer,
          removeChecklistAnswer,
        };

        await syncAnswerChanges(
          templateId,
          project._id,
          previousValues,
          checklist,
          answerSyncDeps
        );

        await createAnswersForNewItems(
          templateId,
          project._id,
          checklistChanges.created,
          createdItemsByUniqueKeyId,
          answerSyncDeps
        );

        await reloadInspectionTemplate();

        const allCurrentNodes = [
          ...sections.filter((s) => s._id).map((s) => ({
            id: s._id!,
            type: 'section' as const,
            order: s.order,
            parentSectionId: s.parentSectionId,
          })),
          ...checklist.filter((i) => i._id).map((i) => ({
            id: i._id!,
            type: 'item' as const,
            order: i.order,
            parentSectionId: i.parentSectionId,
          })),
        ];

        const previousOrderById = new Map<string, string>();
        previousValues.sections.forEach((s) => {
          if (s._id) previousOrderById.set(s._id, s.order);
        });
        previousValues.checklist.forEach((i) => {
          if (i._id) previousOrderById.set(i._id, i.order);
        });

        const nodesWithOrderChange = allCurrentNodes.filter((node) => {
          const prevOrder = previousOrderById.get(node.id);
          return prevOrder && prevOrder !== node.order;
        });

        if (nodesWithOrderChange.length > 0) {
          const nodesByParent = new Map<string | null, typeof allCurrentNodes>();
          allCurrentNodes.forEach((node) => {
            const parentId = node.parentSectionId;
            if (!nodesByParent.has(parentId)) {
              nodesByParent.set(parentId, []);
            }
            nodesByParent.get(parentId)!.push(node);
          });

          nodesByParent.forEach((siblings) => {
            siblings.sort(sortByFractionalOrder);
          });

          for (const node of nodesWithOrderChange) {
            const siblings = nodesByParent.get(node.parentSectionId) || [];
            const nodeIndex = siblings.findIndex((n) => n.id === node.id);
            const previousNode = nodeIndex > 0 ? siblings[nodeIndex - 1] : null;
            const nextNode = nodeIndex < siblings.length - 1 ? siblings[nodeIndex + 1] : null;

            const reorderBeforeId = nextNode?.id;
            const reorderAfterId = previousNode?.id;

            if (node.type === 'section') {
              await reorderChecklistSection({
                _id: node.id,
                projectId: project._id,
                inspectionTemplateId: templateId,
                beforeId: reorderBeforeId,
                afterId: reorderAfterId,
                parentSectionId: node.parentSectionId || undefined,
              });
            } else {
              await reorderInspectionTemplateChecklistItem(
                templateId,
                node.id,
                {
                  beforeId: reorderBeforeId,
                  afterId: reorderAfterId,
                  parentSectionId: node.parentSectionId,
                },
                project._id
              );
            }
          }
        }

        startTemplateUpdateBackgroundSync(
          templateId,
          uploader,
          checklist,
          sections,
          sectionIdMap
        );

        return {
          _id: templateId,
        };
      });
    },
    [
      project._id,
      updateInspectionTemplate,
      updateInspectionTemplateChecklistItem,
      removeInspectionTemplateChecklistItem,
      reorderInspectionTemplateChecklistItem,
      createInspectionTemplateChecklistItems,
      createChecklistSection,
      updateChecklistSection,
      removeChecklistSection,
      reorderChecklistSection,
      createChecklistAnswer,
      updateChecklistAnswer,
      removeChecklistAnswer,
      translate,
      reloadInspectionTemplate,
    ]
  );

  const createTemplate = useCallback(
    (
      template: TemplateValidItem,
      uploader: GU,
      checklist: FormChecklistItem[],
      sections: FormSection[]
    ): Promise<TemplateWizardSubmitResult> => {
      const templateBody = toTemplateWizardDraftPayload(template);

      return createInspectionTemplate(templateBody, project._id)
        .then(async (templateResponse) => {
          const createItemsBatch = async (items: FormChecklistItem[]) => {
            const itemsDto = toCreateChecklistItemsDto(items);
            return createInspectionTemplateChecklistItems(
              templateResponse._id,
              itemsDto,
              project._id
            );
          };

          const { sectionIdMap, createdItemsByUniqueKeyId } =
            await createNodesInOrder(
              templateResponse._id,
              project._id,
              sections,
              checklist,
              createChecklistSection,
              createItemsBatch
            );

          const sectionUploadersByCreatedId = buildSectionUploaders(
            sections,
            sectionIdMap
          );

          const createdItemsForUpload = checklist.map((item) => ({
            _id:
              createdItemsByUniqueKeyId.get(item.uniqueKeyId)?._id ||
              item._id ||
              '',
          }));

          await createAnswersForNewItems(
            templateResponse._id,
            project._id,
            checklist,
            createdItemsByUniqueKeyId,
            {
              translate,
              createChecklistAnswer,
              updateChecklistAnswer,
            }
          );

          startTemplateCreateBackgroundUploads(
            templateResponse._id,
            uploader,
            checklist,
            createdItemsForUpload,
            sectionUploadersByCreatedId
          );

          return templateResponse;
        });
    },
    [
      project._id,
      createInspectionTemplate,
      createInspectionTemplateChecklistItems,
      createChecklistSection,
      createChecklistAnswer,
      updateChecklistAnswer,
      translate,
    ]
  );

  const publishTemplate = useCallback(
    async (id: string): Promise<TemplateWizardSubmitResult> => {
      await publishInspectionTemplate(id, project._id);
      return { _id: id };
    },
    [project._id, publishInspectionTemplate]
  );

  return {
    createTemplate,
    updateTemplate,
    publishTemplate,
  };
}